import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Sparkles, Heart, Gift } from 'lucide-react';
import axios from 'axios';
import ProductCard from '@/components/ProductCard';
import MorseCodeVisualizer from '@/components/MorseCodeVisualizer';
import HomepageReviews from '@/components/HomepageReviews';
import MarketsCalendar from '@/components/MarketsCalendar';
import LatestFromStudio from '@/components/LatestFromStudio';
import logger from '@/utils/logger';
import Seo from '@/components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, testimonialsRes] = await Promise.all([
          axios.get(`${API}/products?featured=true`),
          axios.get(`${API}/testimonials`)
        ]);
        setFeaturedProducts(productsRes.data);
        setTestimonials(testimonialsRes.data);
      } catch (error) {
        logger.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="pt-20">
      <Seo description={"Quiet messages worn close to the skin. Hand-strung Morse code bracelets from Lindy's Paphos studio — from €24.95, free EU shipping."} path="/" />
      {/* Hero Section */}
      <section className="min-h-[90vh] flex items-center" data-testid="hero-section">
        <div className="container-custom w-full px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
            {/* Text Content */}
            <div className="order-2 lg:order-1 text-center lg:text-left">
              <p className="text-caption text-[#84A98C] mb-6 animate-fade-in-up">
                Handcrafted with Intention
              </p>
              <h1 className="heading-display text-5xl md:text-6xl lg:text-7xl text-[#354F52] mb-6 animate-fade-in-up animate-delay-100">
                Wear Your<br />
                <span className="text-[#D4AF37]">Secret Message</span>
              </h1>
              <p className="text-lg md:text-xl text-[#52796F] leading-relaxed mb-8 max-w-lg mx-auto lg:mx-0 animate-fade-in-up animate-delay-200">
                Each bracelet is handmade with beads shaped as dots and dashes, 
                spelling out words that matter most to you.
              </p>
              
              {/* Preview Morse Code */}
              <div className="mb-8 animate-fade-in-up animate-delay-300">
                <p className="text-sm text-[#84A98C] mb-2 uppercase tracking-widest">
                  "Love" in Morse Code
                </p>
                <MorseCodeVisualizer text="LOVE" size="lg" />
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fade-in-up animate-delay-400">
                <Link
                  to="/builder"
                  className="btn-primary inline-flex items-center justify-center gap-2"
                  data-testid="hero-build-your-own"
                >
                  <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                  Build Your Own
                </Link>
                <Link
                  to="/products"
                  className="btn-secondary inline-flex items-center justify-center gap-2"
                  data-testid="hero-shop-now"
                >
                  Shop Now
                  <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </Link>
              </div>
            </div>

            {/* Hero image — Lindy at her workbench in the Paphos studio */}
            <div className="order-1 lg:order-2 relative" data-testid="hero-media">
              <div className="relative aspect-square max-w-md mx-auto lg:max-w-none">
                <img
                  src="/images/lindy-in-studio.jpg"
                  alt="Lindy in her sunlit Paphos studio, hand-stringing a Whispering Wristwear bracelet at her wooden workbench"
                  className="w-full h-full object-cover rounded-sm shadow-float"
                  data-testid="hero-image"
                />
                {/* Floating accent */}
                <div className="absolute -bottom-4 -left-4 bg-[#FAF9F6] p-4 rounded-sm shadow-soft">
                  <div className="flex items-center gap-2 text-[#354F52]">
                    <Sparkles className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
                    <span className="text-sm font-medium">Made with Love</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Design Your Own CTA Section */}
      <section className="py-16 bg-[#354F52]" data-testid="design-cta-section">
        <div className="container-custom px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Bracelet Builder Card */}
            <Link 
              to="/builder"
              className="group bg-[#2F3E46] p-8 md:p-10 rounded-sm hover:bg-[#52796F]/30 transition-all duration-300"
              data-testid="cta-bracelet-builder"
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-7 h-7 text-[#354F52]" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-serif text-2xl text-[#FAF9F6] mb-2 group-hover:text-[#D4AF37] transition-colors">
                    Bracelet Builder
                  </h3>
                  <p className="text-[#CAD2C5] mb-4">
                    Type any word and instantly see it transformed into a beautiful Morse code bracelet. Choose gold, silver, or rose gold beads.
                  </p>
                  <span className="inline-flex items-center gap-2 text-[#D4AF37] text-sm uppercase tracking-widest group-hover:gap-3 transition-all">
                    Try It Now
                    <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                  </span>
                </div>
              </div>
            </Link>

            {/* Custom Order Card */}
            <Link 
              to="/custom"
              className="group bg-[#2F3E46] p-8 md:p-10 rounded-sm hover:bg-[#52796F]/30 transition-all duration-300"
              data-testid="cta-custom-order"
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0">
                  <Heart className="w-7 h-7 text-[#354F52]" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-serif text-2xl text-[#FAF9F6] mb-2 group-hover:text-[#D4AF37] transition-colors">
                    Custom Order
                  </h3>
                  <p className="text-[#CAD2C5] mb-4">
                    Ready to order? Submit your custom bracelet request with your special message and we'll handcraft it just for you.
                  </p>
                  <span className="inline-flex items-center gap-2 text-[#D4AF37] text-sm uppercase tracking-widest group-hover:gap-3 transition-all">
                    Start Your Order
                    <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Product Showcase */}
      <section className="section-padding bg-[#F5F5F0]" data-testid="products-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="text-caption text-[#84A98C] mb-4">Our Collection</p>
            <h2 className="heading-display text-4xl md:text-5xl text-[#354F52]">
              Bestselling Pieces
            </h2>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[3/4] bg-[#CAD2C5] rounded-sm" />
                  <div className="mt-4 h-6 bg-[#CAD2C5] rounded w-3/4 mx-auto" />
                  <div className="mt-2 h-4 bg-[#CAD2C5] rounded w-1/4 mx-auto" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link
              to="/products"
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="view-all-products"
            >
              View All Products
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* Shop by Occasion */}
      <section className="section-padding bg-[#F5F5F0]" data-testid="occasions-section">
        <div className="container-custom px-6 md:px-12 lg:px-24">
          <div className="text-center mb-12 lg:mb-16">
            <p className="text-caption text-[#84A98C] mb-4">Shop by Occasion</p>
            <h2 className="heading-display text-4xl md:text-5xl text-[#354F52] mb-4">
              A bracelet for every story
            </h2>
            <p className="text-[#52796F] max-w-xl mx-auto">
              Find the right whisper of meaning — whether it's a wedding day,
              a new arrival, or a love that lingers.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {[
              { id: 'weddings', label: 'Wedding Gifts', sub: 'For the day two became one', image: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=800&q=80' },
              { id: 'baby-showers', label: 'Baby Showers', sub: 'A whisper for new beginnings', image: 'https://images.unsplash.com/photo-1519689680058-324335c77eba?w=800&q=80' },
              { id: 'in-loving-memory', label: 'In Loving Memory', sub: 'For hearts that remember', image: 'https://images.unsplash.com/photo-1518895949257-7621c3c786d7?w=800&q=80' },
              { id: 'birthdays', label: 'Birthdays', sub: 'Personal, one bead at a time', image: 'https://images.unsplash.com/photo-1513151233558-d860c5398176?w=800&q=80' },
              { id: 'friends', label: 'For Friends', sub: 'Inside jokes, secret promises', image: 'https://images.unsplash.com/photo-1529333166437-7750a6dd5a70?w=800&q=80' },
              { id: 'holidays', label: 'Holidays', sub: 'Season after season', image: 'https://images.unsplash.com/photo-1576919228236-a097c32a5cd4?w=800&q=80' },
            ].map((o) => (
              <Link
                key={o.id}
                to={`/collections/${o.id}`}
                className="group relative aspect-[4/5] md:aspect-[4/3] overflow-hidden rounded-sm shadow-soft hover:shadow-float transition-shadow"
                data-testid={`occasion-${o.id}`}
              >
                <img
                  src={o.image}
                  alt={o.label}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#354F52]/85 via-[#354F52]/30 to-transparent" />
                <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-7 text-[#FAF9F6]">
                  <h3 className="font-serif text-xl md:text-2xl mb-1">
                    {o.label}
                  </h3>
                  <p className="text-xs md:text-sm text-[#CAD2C5] mb-3 italic">
                    {o.sub}
                  </p>
                  <span className="inline-flex items-center gap-1.5 text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity">
                    Shop the collection
                    <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <MarketsCalendar />
      <LatestFromStudio />
      <section id="how-it-works" className="section-padding" data-testid="how-it-works-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="text-caption text-[#84A98C] mb-4">The Process</p>
            <h2 className="heading-display text-4xl md:text-5xl text-[#354F52]">
              How It Works
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-16">
            {/* Step 1 */}
            <div className="text-center">
              <div className="w-20 h-20 bg-[#354F52] rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="w-8 h-8 text-[#D4AF37]" strokeWidth={1.5} />
              </div>
              <p className="text-caption text-[#D4AF37] mb-2">Step 1</p>
              <h3 className="font-serif text-2xl text-[#354F52] mb-4">
                Choose Your Word
              </h3>
              <p className="text-[#52796F] leading-relaxed">
                Pick a word, name, or phrase that holds special meaning. 
                It could be "love," a name, or your favorite mantra.
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center">
              <div className="w-20 h-20 bg-[#354F52] rounded-full flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-8 h-8 text-[#D4AF37]" strokeWidth={1.5} />
              </div>
              <p className="text-caption text-[#D4AF37] mb-2">Step 2</p>
              <h3 className="font-serif text-2xl text-[#354F52] mb-4">
                We Hand-Bead It
              </h3>
              <p className="text-[#52796F] leading-relaxed">
                Our artisans carefully arrange gold and silver beads as 
                dots and dashes to spell your message in Morse code.
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center">
              <div className="w-20 h-20 bg-[#354F52] rounded-full flex items-center justify-center mx-auto mb-6">
                <Gift className="w-8 h-8 text-[#D4AF37]" strokeWidth={1.5} />
              </div>
              <p className="text-caption text-[#D4AF37] mb-2">Step 3</p>
              <h3 className="font-serif text-2xl text-[#354F52] mb-4">
                Receive Your Gift
              </h3>
              <p className="text-[#52796F] leading-relaxed">
                Your bracelet arrives in beautiful packaging, complete 
                with a card explaining the Morse code meaning.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/custom"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="create-your-own"
            >
              Create Your Own
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* About the Maker */}
      <section id="about" className="section-padding bg-[#354F52]" data-testid="about-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="relative">
              <img
                src="/images/lindy-in-studio.jpg"
                alt="Lindy in her sunlit Paphos studio, threading beads onto a Whispering Wristwear bracelet at her wooden workbench"
                className="w-full aspect-[4/3] object-cover rounded-sm shadow-float"
              />
            </div>

            <div className="text-[#FAF9F6]">
              <p className="text-caption text-[#84A98C] mb-4">About the Maker</p>
              <h2 className="heading-display text-4xl md:text-5xl mb-6">
                Crafted with <span className="text-[#D4AF37]">Heart</span>
              </h2>
              <p className="text-lg text-[#CAD2C5] leading-relaxed mb-6">
                Every Whispering Wristwear bracelet is made by hand — sold online
                and at local markets around Paphos, Cyprus.
                We believe that jewelry should tell a story – your story.
              </p>
              <p className="text-[#CAD2C5] leading-relaxed mb-8">
                What started as a gift for a loved one has grown into a passion 
                for creating meaningful pieces. Each bracelet is assembled with 
                care, ensuring that your message is perfectly captured in every 
                dot and dash.
              </p>
              <div className="flex items-center gap-6">
                <div>
                  <p className="text-3xl font-serif text-[#D4AF37]">500+</p>
                  <p className="text-sm text-[#84A98C] uppercase tracking-widest">
                    Happy Customers
                  </p>
                </div>
                <div className="w-px h-12 bg-[#52796F]" />
                <div>
                  <p className="text-3xl font-serif text-[#D4AF37]">100%</p>
                  <p className="text-sm text-[#84A98C] uppercase tracking-widest">
                    Handmade
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <HomepageReviews />

      {/* CTA Section */}
      <section className="section-padding bg-[#F5F5F0]">
        <div className="container-custom text-center">
          <h2 className="heading-display text-4xl md:text-5xl text-[#354F52] mb-6">
            Ready to Create Your<br />
            <span className="text-[#D4AF37]">Secret Message?</span>
          </h2>
          <p className="text-lg text-[#52796F] max-w-lg mx-auto mb-8">
            Whether it's for yourself or a loved one, your story deserves 
            to be worn close to the heart.
          </p>
          <Link
            to="/custom"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="cta-start-creating"
          >
            Start Creating
            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
      </section>
    </div>
  );
}
